import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line, Radar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement,
  RadialLinearScale
);

const TradeRiskAnalyzer = () => {
  const content = article.find((item) => item.id === 7);
  const [activeTab, setActiveTab] = useState("analyzer");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [analysisMode, setAnalysisMode] = useState("comprehensive");
  const [notifications, setNotifications] = useState([]);
  const [calculationHistory, setCalculationHistory] = useState([]);

  const [inputs, setInputs] = useState({
    // Trade Setup
    symbol: "AAPL",
    entryPrice: "150.00",
    stopLoss: "140.00",
    takeProfit: "170.00",
    positionSize: "10000",
    shares: "67",
    
    // Market Context
    currentPrice: "152.50",
    support1: "145.00",
    support2: "140.00",
    resistance1: "160.00",
    resistance2: "170.00",
    volatility: "25.5",
    volume: "25000000",
    
    // Risk Parameters
    accountSize: "50000",
    maxSingleLoss: "2",
    maxPortfolioLoss: "10",
    riskRewardMin: "1.5",
    
    // Technical Factors
    trendDirection: "Bullish",
    trendStrength: "Strong",
    marketCondition: "Normal",
    volumeConfirmation: "Yes",
    
    // Fundamental Factors
    earningsDate: "2024-01-25",
    newsSentiment: "Positive",
    sectorStrength: "Strong",
    marketCap: "2500000000000",
    
    // Position Management
    timeFrame: "Swing",
    leverage: "1",
    correlation: "0.3",
    portfolioDiversity: "75"
  });

  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [tradeScore, setTradeScore] = useState(null);
  const chartRef = useRef();

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "analyzer" || activeTab === "scoring") {
      analyzeTradeRisk();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const analyzeTradeRisk = () => {
    const {
      entryPrice,
      stopLoss,
      takeProfit,
      positionSize,
      shares,
      currentPrice,
      support1,
      support2,
      resistance1,
      resistance2,
      volatility,
      volume,
      accountSize,
      maxSingleLoss,
      maxPortfolioLoss,
      riskRewardMin,
      trendDirection,
      trendStrength,
      marketCondition,
      volumeConfirmation,
      earningsDate,
      newsSentiment,
      sectorStrength,
      timeFrame,
      leverage,
      correlation,
      portfolioDiversity
    } = inputs;

    if (!entryPrice || !stopLoss || !positionSize) {
      return;
    }

    const entry = parseFloat(entryPrice);
    const stop = parseFloat(stopLoss);
    const profit = parseFloat(takeProfit) || 0;
    const position = parseFloat(positionSize);
    const current = parseFloat(currentPrice) || entry;
    const supp1 = parseFloat(support1) || 0;
    const supp2 = parseFloat(support2) || 0;
    const res1 = parseFloat(resistance1) || 0;
    const res2 = parseFloat(resistance2) || 0;
    const vol = parseFloat(volatility) || 20;
    const volVolume = parseFloat(volume) || 1000000;
    const account = parseFloat(accountSize);
    const maxLossPercent = parseFloat(maxSingleLoss) / 100;
    const maxPortfolioPercent = parseFloat(maxPortfolioLoss) / 100;
    const minRiskReward = parseFloat(riskRewardMin);
    const lev = parseFloat(leverage) || 1;
    const corr = parseFloat(correlation) || 0.3;
    const diversity = parseFloat(portfolioDiversity) / 100;

    // Basic Risk Calculations
    const riskPerShare = Math.abs(entry - stop);
    const sharesCount = shares || position / entry;
    const maxLoss = riskPerShare * sharesCount;
    const maxLossPercentOfPosition = (maxLoss / position) * 100;
    const maxLossPercentOfAccount = (maxLoss / account) * 100;

    // Risk-Reward Analysis
    const potentialProfit = profit ? (profit - entry) * sharesCount : 0;
    const riskRewardRatio = profit ? Math.abs((profit - entry) / (entry - stop)) : 0;
    const profitPercent = (potentialProfit / position) * 100;

    // Position Sizing Validation
    const maxAllowedLoss = account * maxLossPercent;
    const isSizeAppropriate = maxLoss <= maxAllowedLoss;
    const sizeScore = isSizeAppropriate ? 100 : (maxAllowedLoss / maxLoss) * 100;

    // Technical Analysis Scoring
    const technicalScore = calculateTechnicalScore(
      current, entry, stop, profit, supp1, supp2, res1, res2,
      trendDirection, trendStrength, volumeConfirmation
    );

    // Market Context Scoring
    const marketScore = calculateMarketScore(
      marketCondition, vol, volVolume, earningsDate, newsSentiment, sectorStrength
    );

    // Risk Management Scoring
    const riskManagementScore = calculateRiskManagementScore(
      maxLossPercentOfAccount, riskRewardRatio, minRiskReward,
      lev, corr, diversity, timeFrame
    );

    // Support/Resistance Analysis
    const supportAnalysis = analyzeSupportResistance(
      entry, stop, supp1, supp2, res1, res2
    );

    // Volatility Analysis
    const volatilityAnalysis = analyzeVolatility(vol, riskPerShare, entry);

    // Overall Trade Score
    const overallScore = (technicalScore + marketScore + riskManagementScore) / 3;
    const tradeQuality = getTradeQuality(overallScore);

    const analysis = {
      basic: {
        riskPerShare: riskPerShare,
        shares: sharesCount,
        maxLoss: maxLoss,
        maxLossPercent: maxLossPercentOfPosition,
        maxLossAccountPercent: maxLossPercentOfAccount,
        potentialProfit: potentialProfit,
        profitPercent: profitPercent,
        riskRewardRatio: riskRewardRatio
      },
      validation: {
        maxAllowedLoss: maxAllowedLoss,
        isSizeAppropriate: isSizeAppropriate,
        sizeScore: sizeScore,
        meetsRiskReward: riskRewardRatio >= minRiskReward,
        positionSizeScore: (position / account) * 100
      },
      technical: {
        score: technicalScore,
        trendAlignment: calculateTrendAlignment(trendDirection, entry, current),
        supportDistance: ((entry - Math.max(supp1, supp2)) / entry) * 100,
        resistanceDistance: ((Math.min(res1, res2) - entry) / entry) * 100,
        volumeScore: volumeConfirmation === "Yes" ? 100 : 50
      },
      market: {
        score: marketScore,
        volatilityImpact: (vol / 20) * 100, // Normalized to 20% baseline
        earningsProximity: calculateEarningsProximity(earningsDate),
        sentimentScore: getSentimentScore(newsSentiment),
        sectorScore: getSectorScore(sectorStrength)
      },
      risk: {
        score: riskManagementScore,
        leverageImpact: lev > 1 ? (1 / lev) * 100 : 100,
        correlationRisk: corr * 100,
        diversityScore: diversity * 100,
        timeFrameScore: getTimeFrameScore(timeFrame)
      },
      supportResistance: supportAnalysis,
      volatility: volatilityAnalysis,
      overall: {
        score: overallScore,
        quality: tradeQuality.level,
        color: tradeQuality.color,
        recommendation: tradeQuality.recommendation,
        confidence: tradeQuality.confidence
      }
    };

    setRiskAnalysis(analysis);
    calculateTradeScore(analysis);
  };

  const calculateTechnicalScore = (current, entry, stop, profit, supp1, supp2, res1, res2, trend, strength, volume) => {
    let score = 50;

    // Trend alignment
    if (trend === "Bullish" && entry < current) score += 20;
    else if (trend === "Bearish" && entry > current) score += 20;
    else score -= 10;

    // Trend strength
    if (strength === "Strong") score += 15;
    else if (strength === "Moderate") score += 5;
    else score -= 10;

    // Support/resistance levels
    const nearestSupport = Math.max(supp1, supp2);
    const nearestResistance = Math.min(res1, res2);
    
    if (entry > nearestSupport && entry < nearestResistance) score += 15;
    if (stop < nearestSupport) score += 10;
    if (profit && profit < nearestResistance) score += 10;

    // Volume confirmation
    if (volume === "Yes") score += 10;

    return Math.max(0, Math.min(100, score));
  };

  const calculateMarketScore = (condition, volatility, volume, earnings, sentiment, sector) => {
    let score = 50;

    // Market condition
    const conditionScores = { "Bull": 20, "Normal": 10, "Bear": -10, "Volatile": -5 };
    score += conditionScores[condition] || 0;

    // Volatility adjustment
    if (volatility > 30) score -= 15;
    else if (volatility < 15) score += 5;

    // Volume (normalized)
    const normalizedVolume = Math.min(volume / 5000000, 1); // 5M volume = 100%
    score += normalizedVolume * 10;

    // Earnings proximity
    score -= calculateEarningsProximity(earnings) * 20;

    // Sentiment
    const sentimentScores = { "Very Positive": 15, "Positive": 10, "Neutral": 0, "Negative": -10, "Very Negative": -20 };
    score += sentimentScores[sentiment] || 0;

    // Sector strength
    const sectorScores = { "Very Strong": 15, "Strong": 10, "Neutral": 0, "Weak": -10, "Very Weak": -20 };
    score += sectorScores[sector] || 0;

    return Math.max(0, Math.min(100, score));
  };

  const calculateRiskManagementScore = (lossPercent, riskReward, minRiskReward, leverage, correlation, diversity, timeFrame) => {
    let score = 50;

    // Loss percentage
    if (lossPercent <= 1) score += 20;
    else if (lossPercent <= 2) score += 10;
    else if (lossPercent <= 3) score += 5;
    else if (lossPercent > 5) score -= 15;

    // Risk-reward ratio
    if (riskReward >= minRiskReward) score += 20;
    else if (riskReward >= 1) score += 10;
    else score -= 20;

    // Leverage
    if (leverage === 1) score += 10;
    else if (leverage <= 2) score += 5;
    else if (leverage > 3) score -= 15;

    // Correlation
    score += (1 - correlation) * 10;

    // Diversity
    score += diversity * 10;

    // Time frame appropriateness
    score += getTimeFrameScore(timeFrame);

    return Math.max(0, Math.min(100, score));
  };

  const calculateTrendAlignment = (trend, entry, current) => {
    if (trend === "Bullish" && entry <= current) return "Aligned";
    if (trend === "Bearish" && entry >= current) return "Aligned";
    return "Counter-trend";
  };

  const calculateEarningsProximity = (earningsDate) => {
    if (!earningsDate) return 0;
    const earnings = new Date(earningsDate);
    const today = new Date();
    const diffTime = Math.abs(earnings - today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, 1 - (diffDays / 30)); // 0 if >30 days, 1 if today
  };

  const getSentimentScore = (sentiment) => {
    const scores = { "Very Positive": 100, "Positive": 80, "Neutral": 50, "Negative": 20, "Very Negative": 0 };
    return scores[sentiment] || 50;
  };

  const getSectorScore = (sector) => {
    const scores = { "Very Strong": 100, "Strong": 80, "Neutral": 50, "Weak": 20, "Very Weak": 0 };
    return scores[sector] || 50;
  };

  const getTimeFrameScore = (timeFrame) => {
    const scores = { "Intraday": 70, "Swing": 80, "Position": 90, "Long-term": 100 };
    return scores[timeFrame] || 80;
  };

  const analyzeSupportResistance = (entry, stop, supp1, supp2, res1, res2) => {
    const nearestSupport = Math.max(supp1, supp2, 0);
    const nearestResistance = Math.min(res1, res2, entry * 2); // Cap at 2x entry
    
    return {
      supportDistance: entry - nearestSupport,
      supportDistancePercent: ((entry - nearestSupport) / entry) * 100,
      resistanceDistance: nearestResistance - entry,
      resistanceDistancePercent: ((nearestResistance - entry) / entry) * 100,
      stopToSupportRatio: nearestSupport > 0 ? (entry - stop) / (entry - nearestSupport) : 1,
      isStopBelowSupport: stop <= nearestSupport,
      isTargetBelowResistance: nearestResistance > 0 && parseFloat(inputs.takeProfit) <= nearestResistance
    };
  };

  const analyzeVolatility = (volatility, riskPerShare, entryPrice) => {
    const dailyVolatility = (volatility / 100) / Math.sqrt(252);
    const expectedDailyMove = entryPrice * dailyVolatility;
    
    return {
      volatility: volatility,
      dailyMove: expectedDailyMove,
      riskToVolatilityRatio: riskPerShare / expectedDailyMove,
      isRiskAppropriate: riskPerShare <= expectedDailyMove * 2,
      volatilityZone: volatility < 20 ? "Low" : volatility < 40 ? "Medium" : "High"
    };
  };

  const getTradeQuality = (score) => {
    if (score >= 80) return { level: "Excellent", color: "#28A745", recommendation: "Strong Buy", confidence: "High" };
    if (score >= 70) return { level: "Good", color: "#2B8C3A", recommendation: "Buy", confidence: "Medium-High" };
    if (score >= 60) return { level: "Fair", color: "#FFC107", recommendation: "Consider", confidence: "Medium" };
    if (score >= 50) return { level: "Marginal", color: "#FF6B6B", recommendation: "Avoid", confidence: "Low" };
    return { level: "Poor", color: "#E53E3E", recommendation: "Reject", confidence: "Very Low" };
  };

  const calculateTradeScore = (analysis) => {
    const scoreBreakdown = {
      technical: analysis.technical.score,
      market: analysis.market.score,
      risk: analysis.risk.score,
      position: analysis.validation.sizeScore,
      timing: analysis.technical.trendAlignment === "Aligned" ? 80 : 40
    };

    setTradeScore(scoreBreakdown);
  };

  const generateRecommendations = (analysis) => {
    const recommendations = [];

    if (!analysis.validation.isSizeAppropriate) {
      recommendations.push({
        type: "danger",
        message: "Position size too large for risk limits",
        action: `Reduce position to $${analysis.validation.maxAllowedLoss.toFixed(0)} or less`
      });
    }

    if (!analysis.validation.meetsRiskReward) {
      recommendations.push({
        type: "warning",
        message: "Risk-reward ratio below minimum requirement",
        action: `Adjust targets to achieve at least ${inputs.riskRewardMin}:1 ratio`
      });
    }

    if (analysis.technical.trendAlignment === "Counter-trend") {
      recommendations.push({
        type: "warning",
        message: "Trading counter to trend direction",
        action: "Consider waiting for trend alignment or reduce position size"
      });
    }

    if (analysis.volatility.riskToVolatilityRatio > 2) {
      recommendations.push({
        type: "info",
        message: "Stop loss wider than typical daily moves",
        action: "Consider tightening stop loss to avoid normal volatility"
      });
    }

    if (analysis.supportResistance.isStopBelowSupport) {
      recommendations.push({
        type: "success",
        message: "Stop loss placed below key support",
        action: "Good risk management practice"
      });
    }

    if (analysis.overall.score >= 70) {
      recommendations.push({
        type: "success",
        message: "Trade meets quality criteria",
        action: "Proceed with trade as planned"
      });
    }

    return recommendations;
  };

  const resetCalculator = () => {
    setInputs({
      symbol: "AAPL",
      entryPrice: "150.00",
      stopLoss: "140.00",
      takeProfit: "170.00",
      positionSize: "10000",
      shares: "67",
      currentPrice: "152.50",
      support1: "145.00",
      support2: "140.00",
      resistance1: "160.00",
      resistance2: "170.00",
      volatility: "25.5",
      volume: "25000000",
      accountSize: "50000",
      maxSingleLoss: "2",
      maxPortfolioLoss: "10",
      riskRewardMin: "1.5",
      trendDirection: "Bullish",
      trendStrength: "Strong",
      marketCondition: "Normal",
      volumeConfirmation: "Yes",
      earningsDate: "2024-01-25",
      newsSentiment: "Positive",
      sectorStrength: "Strong",
      marketCap: "2500000000000",
      timeFrame: "Swing",
      leverage: "1",
      correlation: "0.3",
      portfolioDiversity: "75"
    });
    addNotification("Analyzer reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!riskAnalysis) return;
    
    const csvContent = [
      "Metric,Value,Score",
      `Trade Quality,${riskAnalysis.overall.quality},${riskAnalysis.overall.score.toFixed(1)}`,
      `Recommendation,${riskAnalysis.overall.recommendation},${riskAnalysis.overall.confidence}`,
      `Risk-Reward Ratio,${riskAnalysis.basic.riskRewardRatio.toFixed(2)},${riskAnalysis.validation.meetsRiskReward ? 'Pass' : 'Fail'}`,
      `Maximum Loss,$${riskAnalysis.basic.maxLoss.toFixed(2)},${riskAnalysis.basic.maxLossAccountPercent.toFixed(2)}%`,
      `Position Size,${riskAnalysis.validation.isSizeAppropriate ? 'Appropriate' : 'Too Large'},${riskAnalysis.validation.sizeScore.toFixed(1)}`,
      `Technical Score,N/A,${riskAnalysis.technical.score.toFixed(1)}`,
      `Market Score,N/A,${riskAnalysis.market.score.toFixed(1)}`,
      `Risk Management Score,N/A,${riskAnalysis.risk.score.toFixed(1)}`,
      `Trend Alignment,${riskAnalysis.technical.trendAlignment},${riskAnalysis.technical.trendAlignment === 'Aligned' ? 'Good' : 'Poor'}`,
      `Volatility Impact,${riskAnalysis.volatility.volatilityZone},${riskAnalysis.volatility.isRiskAppropriate ? 'Appropriate' : 'Review'}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trade-risk-analysis.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Analysis exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!riskAnalysis) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      analysis: riskAnalysis
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Analysis saved to history", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { maxSingleLoss: "1", riskRewardMin: "2", leverage: "1" },
      moderate: { maxSingleLoss: "2", riskRewardMin: "1.5", leverage: "1" },
      aggressive: { maxSingleLoss: "3", riskRewardMin: "1", leverage: "2" }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} risk profile applied`, "info");
  };

  const strategyPreset = (strategy) => {
    const presets = {
      breakout: { 
        trendDirection: "Bullish", 
        trendStrength: "Strong",
        marketCondition: "Normal",
        timeFrame: "Swing"
      },
      pullback: { 
        trendDirection: "Bullish", 
        trendStrength: "Moderate",
        marketCondition: "Normal", 
        timeFrame: "Swing"
      },
      reversal: { 
        trendDirection: "Bearish", 
        trendStrength: "Weak",
        marketCondition: "Volatile", 
        timeFrame: "Intraday"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[strategy] }));
    addNotification(`${strategy} trading strategy applied`, "info");
  };

  // Chart Data
  const scoreRadarData = tradeScore ? {
    labels: ['Technical', 'Market', 'Risk Mgmt', 'Position', 'Timing'],
    datasets: [{
      label: 'Trade Score',
      data: [
        tradeScore.technical,
        tradeScore.market,
        tradeScore.risk,
        tradeScore.position,
        tradeScore.timing
      ],
      backgroundColor: 'rgba(25, 105, 44, 0.2)',
      borderColor: '#19692C',
      pointBackgroundColor: '#19692C',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: '#19692C'
    }]
  } : null;

  const riskRewardData = riskAnalysis ? {
    labels: ['Potential Loss', 'Potential Profit'],
    datasets: [{
      data: [
        Math.abs(riskAnalysis.basic.maxLoss),
        Math.max(riskAnalysis.basic.potentialProfit, 0)
      ],
      backgroundColor: [
        '#FF6B6B',
        '#28A745'
      ],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const componentScoreData = riskAnalysis ? {
    labels: ['Technical Analysis', 'Market Context', 'Risk Management'],
    datasets: [{
      label: 'Component Scores',
      data: [
        riskAnalysis.technical.score,
        riskAnalysis.market.score,
        riskAnalysis.risk.score
      ],
      backgroundColor: [
        'rgba(25, 105, 44, 0.6)',
        'rgba(40, 167, 69, 0.6)',
        'rgba(255, 193, 7, 0.6)'
      ],
      borderColor: '#19692C',
      borderWidth: 2
    }]
  } : null;

  const tradeQuality = riskAnalysis ? riskAnalysis.overall : null;
  const recommendations = riskAnalysis ? generateRecommendations(riskAnalysis) : [];

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Trade Risk Analyzer</h1>
        </div>
        <p>Comprehensive trade analysis with multi-factor scoring and risk assessment</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "analyzer" ? "active" : ""}`}
                onClick={() => setActiveTab("analyzer")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Analyzer' : 'Risk Analyzer'}
              </button>
              <button 
                className={`tab ${activeTab === "scoring" ? "active" : ""}`}
                onClick={() => setActiveTab("scoring")}
              >
                <i className="fas fa-star"></i>
                {isMobile ? 'Scoring' : 'Trade Scoring'}
              </button>
              <button 
                className={`tab ${activeTab === "recommendations" ? "active" : ""}`}
                onClick={() => setActiveTab("recommendations")}
              >
                <i className="fas fa-lightbulb"></i>
                {isMobile ? 'Advice' : 'Recommendations'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Risk Profile Presets */}
          <div className="presets-section">
            <h4>Risk Profile:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Strategy Selection */}
          <div className="mode-selection">
            <div className="mode-buttons">
              <button 
                className={`mode-btn ${analysisMode === "comprehensive" ? "active" : ""}`}
                onClick={() => setAnalysisMode("comprehensive")}
              >
                <i className="fas fa-chart-bar"></i>
                Comprehensive
              </button>
              <button 
                className={`mode-btn ${analysisMode === "technical" ? "active" : ""}`}
                onClick={() => setAnalysisMode("technical")}
              >
                <i className="fas fa-chart-line"></i>
                Technical Focus
              </button>
              <button 
                className={`mode-btn ${analysisMode === "risk" ? "active" : ""}`}
                onClick={() => setAnalysisMode("risk")}
              >
                <i className="fas fa-shield-alt"></i>
                Risk Focus
              </button>
            </div>
            <div className="mode-description">
              {analysisMode === "comprehensive" 
                ? "Full analysis including technical, fundamental, and risk factors"
                : analysisMode === "technical"
                ? "Focus on chart patterns, trends, and technical indicators"
                : "Emphasis on risk management, position sizing, and portfolio impact"
              }
            </div>
          </div>

          {/* Strategy Presets */}
          <div className="presets-section">
            <h4>Trading Strategy:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => strategyPreset('breakout')}>
                Breakout
              </button>
              <button className="preset-btn moderate" onClick={() => strategyPreset('pullback')}>
                Pullback
              </button>
              <button className="preset-btn aggressive" onClick={() => strategyPreset('reversal')}>
                Reversal
              </button>
            </div>
          </div>

          {/* Risk Analyzer Tab */}
          {activeTab === "analyzer" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-trade"></i>
                    Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-tag"></i> Symbol</label>
                      <input 
                        type="text" 
                        name="symbol" 
                        value={inputs.symbol} 
                        onChange={handleChange}
                        placeholder="AAPL"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-right"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="150.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop Loss</label>
                      <input 
                        type="number" 
                        name="stopLoss" 
                        value={inputs.stopLoss} 
                        onChange={handleChange}
                        placeholder="140.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Take Profit</label>
                      <input 
                        type="number" 
                        name="takeProfit" 
                        value={inputs.takeProfit} 
                        onChange={handleChange}
                        placeholder="170.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Market Context
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Current Price</label>
                      <input 
                        type="number" 
                        name="currentPrice" 
                        value={inputs.currentPrice} 
                        onChange={handleChange}
                        placeholder="152.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Support 1</label>
                      <input 
                        type="number" 
                        name="support1" 
                        value={inputs.support1} 
                        onChange={handleChange}
                        placeholder="145.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-shield-alt"></i> Support 2</label>
                      <input 
                        type="number" 
                        name="support2" 
                        value={inputs.support2} 
                        onChange={handleChange}
                        placeholder="140.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Resistance 1</label>
                      <input 
                        type="number" 
                        name="resistance1" 
                        value={inputs.resistance1} 
                        onChange={handleChange}
                        placeholder="160.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-flag"></i> Resistance 2</label>
                      <input 
                        type="number" 
                        name="resistance2" 
                        value={inputs.resistance2} 
                        onChange={handleChange}
                        placeholder="170.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-wave-square"></i> Volatility (%)</label>
                      <input 
                        type="number" 
                        name="volatility" 
                        value={inputs.volatility} 
                        onChange={handleChange}
                        placeholder="25.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-bar"></i> Volume</label>
                      <input 
                        type="number" 
                        name="volume" 
                        value={inputs.volume} 
                        onChange={handleChange}
                        placeholder="25000000"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Risk Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-wallet"></i> Account Size</label>
                      <input 
                        type="number" 
                        name="accountSize" 
                        value={inputs.accountSize} 
                        onChange={handleChange}
                        placeholder="50000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Position Size</label>
                      <input 
                        type="number" 
                        name="positionSize" 
                        value={inputs.positionSize} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exclamation-triangle"></i> Max Single Loss (%)</label>
                      <input 
                        type="number" 
                        name="maxSingleLoss" 
                        value={inputs.maxSingleLoss} 
                        onChange={handleChange}
                        placeholder="2"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-balance-scale"></i> Min Risk/Reward</label>
                      <input 
                        type="number" 
                        name="riskRewardMin" 
                        value={inputs.riskRewardMin} 
                        onChange={handleChange}
                        placeholder="1.5"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save Analysis
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Trade Scoring Tab */}
          {activeTab === "scoring" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-star"></i>
                  Technical Factors
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-arrow-trend-up"></i> Trend Direction</label>
                    <select name="trendDirection" value={inputs.trendDirection} onChange={handleChange}>
                      <option value="Bullish">Bullish</option>
                      <option value="Bearish">Bearish</option>
                      <option value="Sideways">Sideways</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-bolt"></i> Trend Strength</label>
                    <select name="trendStrength" value={inputs.trendStrength} onChange={handleChange}>
                      <option value="Very Strong">Very Strong</option>
                      <option value="Strong">Strong</option>
                      <option value="Moderate">Moderate</option>
                      <option value="Weak">Weak</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-bar"></i> Volume Confirmation</label>
                    <select name="volumeConfirmation" value={inputs.volumeConfirmation} onChange={handleChange}>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Mixed">Mixed</option>
                    </select>
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-globe"></i>
                  Market Factors
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-chart-line"></i> Market Condition</label>
                    <select name="marketCondition" value={inputs.marketCondition} onChange={handleChange}>
                      <option value="Bull">Bull Market</option>
                      <option value="Normal">Normal</option>
                      <option value="Bear">Bear Market</option>
                      <option value="Volatile">High Volatility</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-calendar"></i> Earnings Date</label>
                    <input 
                      type="date" 
                      name="earningsDate" 
                      value={inputs.earningsDate} 
                      onChange={handleChange}
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-newspaper"></i> News Sentiment</label>
                    <select name="newsSentiment" value={inputs.newsSentiment} onChange={handleChange}>
                      <option value="Very Positive">Very Positive</option>
                      <option value="Positive">Positive</option>
                      <option value="Neutral">Neutral</option>
                      <option value="Negative">Negative</option>
                      <option value="Very Negative">Very Negative</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-industry"></i> Sector Strength</label>
                    <select name="sectorStrength" value={inputs.sectorStrength} onChange={handleChange}>
                      <option value="Very Strong">Very Strong</option>
                      <option value="Strong">Strong</option>
                      <option value="Neutral">Neutral</option>
                      <option value="Weak">Weak</option>
                      <option value="Very Weak">Very Weak</option>
                    </select>
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-cogs"></i>
                  Position Management
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-clock"></i> Time Frame</label>
                    <select name="timeFrame" value={inputs.timeFrame} onChange={handleChange}>
                      <option value="Intraday">Intraday</option>
                      <option value="Swing">Swing</option>
                      <option value="Position">Position</option>
                      <option value="Long-term">Long-term</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                    <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                      <option value="1">1x (No Leverage)</option>
                      <option value="2">2x</option>
                      <option value="3">3x</option>
                      <option value="5">5x</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-link"></i> Correlation</label>
                    <select name="correlation" value={inputs.correlation} onChange={handleChange}>
                      <option value="0.1">Low (0.1)</option>
                      <option value="0.3">Medium (0.3)</option>
                      <option value="0.5">High (0.5)</option>
                      <option value="0.7">Very High (0.7)</option>
                    </select>
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-sitemap"></i> Portfolio Diversity (%)</label>
                    <input 
                      type="number" 
                      name="portfolioDiversity" 
                      value={inputs.portfolioDiversity} 
                      onChange={handleChange}
                      placeholder="75"
                      step="1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Analysis History
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No analyses yet. Start analyzing trades to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className="history-profit" style={{ color: calc.analysis.overall.color }}>
                          <i className="fas fa-star"></i>
                          {calc.analysis.overall.score.toFixed(1)} - {calc.analysis.overall.quality}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-balance-scale"></i> R:R: {calc.analysis.basic.riskRewardRatio.toFixed(2)}</span>
                        <span><i className="fas fa-exclamation-triangle"></i> Loss: {calc.analysis.basic.maxLossAccountPercent.toFixed(1)}%</span>
                        <span><i className="fas fa-check-circle"></i> {calc.analysis.overall.recommendation}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "analyzer" || activeTab === "scoring" || activeTab === "recommendations") && (
          <div className="right-panel">
            {activeTab === "analyzer" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-line"></i>
                    Trade Risk Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {riskAnalysis ? (
                  <>
                    {/* Trade Quality Summary */}
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value" style={{ color: '#FFFFFF' }}>
                          {tradeQuality.score.toFixed(1)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-star"></i>
                          Overall Score
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ color: tradeQuality.color }}>
                          {tradeQuality.quality}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-flag"></i>
                          Trade Quality
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {riskAnalysis.basic.riskRewardRatio.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Risk/Reward
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value" style={{ 
                          color: riskAnalysis.validation.isSizeAppropriate ? '#28A745' : '#E53E3E' 
                        }}>
                          {riskAnalysis.basic.maxLossAccountPercent.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-exclamation-triangle"></i>
                          Account Risk
                        </div>
                      </div>
                    </div>

                    {/* Trade Recommendation */}
                    <div className="risk-summary">
                      <h4>Trade Recommendation: <span style={{ color: tradeQuality.color }}>{tradeQuality.recommendation}</span></h4>
                      <div className="risk-meter">
                        <div className="meter-bar">
                          <div 
                            className="meter-fill"
                            style={{ 
                              width: `${tradeQuality.score}%`,
                              backgroundColor: tradeQuality.color
                            }}
                          ></div>
                        </div>
                        <div className="meter-value" style={{ color: tradeQuality.color }}>
                          Confidence: {tradeQuality.confidence}
                        </div>
                      </div>
                    </div>

                    {/* Risk-Reward Analysis */}
                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-down"></i> Maximum Loss:</span>
                        <span>${riskAnalysis.basic.maxLoss.toFixed(2)} ({riskAnalysis.basic.maxLossPercent.toFixed(2)}%)</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-arrow-up"></i> Potential Profit:</span>
                        <span>${riskAnalysis.basic.potentialProfit.toFixed(2)} ({riskAnalysis.basic.profitPercent.toFixed(2)}%)</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-balance-scale"></i> Risk-Reward Ratio:</span>
                        <span style={{ 
                          color: riskAnalysis.validation.meetsRiskReward ? '#28A745' : '#E53E3E'
                        }}>
                          {riskAnalysis.basic.riskRewardRatio.toFixed(2)}:1
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-ruler"></i> Stop Distance:</span>
                        <span>{riskAnalysis.basic.riskPerShare.toFixed(2)} points ({((riskAnalysis.basic.riskPerShare / parseFloat(inputs.entryPrice)) * 100).toFixed(2)}%)</span>
                      </div>
                    </div>

                    {/* Risk-Reward Visualization */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Risk-Reward Balance
                      </h3>
                      <Doughnut 
                        data={riskRewardData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Component Scores */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Analysis Component Scores
                      </h3>
                      <Bar 
                        data={componentScoreData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              max: 100,
                              ticks: {
                                callback: function(value) {
                                  return value + '%';
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-chart-line fa-3x"></i>
                    <p>Enter trade details to analyze risk and quality</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scoring" && tradeScore && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-star"></i>
                  Detailed Trade Scoring
                </h3>

                {/* Component Scores */}
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#19692C' }}>
                      {riskAnalysis.technical.score.toFixed(1)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Technical Score
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#28A745' }}>
                      {riskAnalysis.market.score.toFixed(1)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-globe"></i>
                      Market Score
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ color: '#FFC107' }}>
                      {riskAnalysis.risk.score.toFixed(1)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-shield-alt"></i>
                      Risk Score
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value" style={{ 
                      color: riskAnalysis.validation.isSizeAppropriate ? '#28A745' : '#E53E3E' 
                    }}>
                      {riskAnalysis.validation.sizeScore.toFixed(1)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-balance-scale"></i>
                      Position Score
                    </div>
                  </div>
                </div><br/>

                {/* Radar Chart */}
                <div className="chart-container">
                  <h3>
                    <i className="fas fa-bullseye"></i>
                    Trade Score Radar
                  </h3>
                  <Radar 
                    data={scoreRadarData}
                    options={{
                      responsive: true,
                      scales: {
                        r: {
                          beginAtZero: true,
                          max: 100,
                          ticks: {
                            stepSize: 20
                          }
                        }
                      }
                    }}
                  />
                </div>

                {/* Detailed Scoring Breakdown */}
                <div className="risk-summary">
                  <h4>Scoring Breakdown</h4>
                  <div className="risk-metrics">
                    <div className="risk-metric">
                      <span className="metric-label">Trend Alignment:</span>
                      <span className="metric-value" style={{ 
                        color: riskAnalysis.technical.trendAlignment === 'Aligned' ? '#28A745' : '#E53E3E'
                      }}>
                        {riskAnalysis.technical.trendAlignment}
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Support Distance:</span>
                      <span className="metric-value">
                        {riskAnalysis.technical.supportDistance.toFixed(1)}%
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Volatility Impact:</span>
                      <span className="metric-value">
                        {riskAnalysis.volatility.volatilityZone}
                      </span>
                    </div>
                    <div className="risk-metric">
                      <span className="metric-label">Position Size:</span>
                      <span className="metric-value" style={{ 
                        color: riskAnalysis.validation.isSizeAppropriate ? '#28A745' : '#E53E3E'
                      }}>
                        {riskAnalysis.validation.isSizeAppropriate ? 'Appropriate' : 'Too Large'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "recommendations" && riskAnalysis && (
              <div className="risk-results-panel">
                <h3>
                  <i className="fas fa-lightbulb"></i>
                  Trade Recommendations
                </h3><br/>
                
                {/* Actionable Recommendations */}
                <div className="scenario-comparison">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="scenario-item">
                      <div className="scenario-name">
                        <i className={`fas fa-${rec.type === 'success' ? 'check' : rec.type === 'warning' ? 'exclamation-triangle' : 'exclamation-circle'} fa-xs`}></i>
                        {rec.message}
                      </div>
                      <div className="scenario-outcome">{rec.action}</div>
                    </div>
                  ))}
                </div>

                {/* Improvement Suggestions */}
                <div className="risk-summary">
                  <h4>Areas for Improvement</h4>
                  <div className="risk-metrics">
                    {riskAnalysis.technical.score < 70 && (
                      <div className="risk-metric">
                        <span className="metric-label">Technical Analysis:</span>
                        <span className="metric-value" style={{ color: '#FF6B6B' }}>
                          Needs improvement ({riskAnalysis.technical.score.toFixed(1)}/100)
                        </span>
                      </div>
                    )}
                    {riskAnalysis.market.score < 70 && (
                      <div className="risk-metric">
                        <span className="metric-label">Market Context:</span>
                        <span className="metric-value" style={{ color: '#FF6B6B' }}>
                          Review conditions ({riskAnalysis.market.score.toFixed(1)}/100)
                        </span>
                      </div>
                    )}
                    {riskAnalysis.risk.score < 70 && (
                      <div className="risk-metric">
                        <span className="metric-label">Risk Management:</span>
                        <span className="metric-value" style={{ color: '#FF6B6B' }}>
                          Strengthen approach ({riskAnalysis.risk.score.toFixed(1)}/100)
                        </span>
                      </div>
                    )}
                    {riskAnalysis.overall.score >= 70 && (
                      <div className="risk-metric">
                        <span className="metric-label">Overall Assessment:</span>
                        <span className="metric-value" style={{ color: '#28A745' }}>
                          Good trade setup
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Final Decision Support */}
                <div className="risk-summary">
                  <h4>Final Decision Support</h4>
                  <div className="scenario-list">
                    <div className="scenario-item">
                      <div className="scenario-name">
                        <i className="fas fa-thumbs-up fa-xs" style={{ color: tradeQuality.color }}></i>
                        Trade Quality: {tradeQuality.quality}
                      </div>
                      <div className="scenario-outcome">
                        {tradeQuality.recommendation} - {tradeQuality.confidence} Confidence
                      </div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">
                        <i className="fas fa-balance-scale fa-xs"></i>
                        Risk-Reward Assessment
                      </div>
                      <div className="scenario-outcome">
                        {riskAnalysis.basic.riskRewardRatio.toFixed(2)}:1 ratio - {
                          riskAnalysis.validation.meetsRiskReward ? 'Meets' : 'Below'
                        } minimum requirement
                      </div>
                    </div>
                    <div className="scenario-item">
                      <div className="scenario-name">
                        <i className="fas fa-chart-line fa-xs"></i>
                        Market Conditions
                      </div>
                      <div className="scenario-outcome">
                        {inputs.marketCondition} market with {inputs.trendStrength.toLowerCase()} {inputs.trendDirection.toLowerCase()} trend
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default TradeRiskAnalyzer;